// RootBinomialOpenTask.js
// (OTWARTE) - wyrazenie typu (sqrt(p) +/- b)^2 +/- sqrt(c^2 * p)
// po uproszczeniu do postaci A +/- B*sqrt(p).

import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import SqrtInput from "../../../../components/inputs/SqrtInput";

function pick(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

function coerceToString(val) {
  if (val == null) return "";
  if (typeof val === "string") return val;
  if (typeof val === "number" || typeof val === "boolean") return String(val);
  if (typeof val === "object" && val.target && "value" in val.target) {
    return String(val.target.value ?? "");
  }
  if (typeof val === "object" && "raw" in val) return String(val.raw ?? "");
  if (typeof val === "object" && "latex" in val) return String(val.latex ?? "");
  if (typeof val === "object" && "text" in val) return String(val.text ?? "");
  if (typeof val === "object" && "expr" in val) return String(val.expr ?? "");
  if (typeof val === "object" && "value" in val && typeof val.value !== "object") {
    return String(val.value ?? "");
  }
  return "";
}

function normalizeCanonical(str) {
  return String(str ?? "")
    .replace(/\u200b/g, "")
    .replace(/[\u0000-\u001F\u007F-\u009F]/g, "")
    .replace(/\s+/g, "")
    .replace(/,/g, ".")
    .replace(/[−–]/g, "-")
    .replace(/\\\\sqrt/g, "√")
    .replace(/\\sqrt/g, "√")
    .replace(/sqrt/g, "√")
    .replace(/√\{([^}]+)\}/g, "√$1")
    .replace(/√\(([^)]+)\)/g, "√$1")
    .trim();
}

function evaluateExpression(expr) {
  const cleaned = normalizeCanonical(expr);
  if (!cleaned) return NaN;
  try {
    const parsed = cleaned
      .replace(/(\d*)√(\d+)/g, (_, a, b) => `${a || "1"}*Math.sqrt(${b})`)
      .replace(/√(\d+)/g, (_, b) => `Math.sqrt(${b})`);
    return Function(`return ${parsed}`)();
  } catch {
    return NaN;
  }
}

function formatRootExpr(constTerm, alpha, p) {
  if (alpha === 0) return String(constTerm);
  const absA = Math.abs(alpha);
  const rootPart = absA === 1 ? `\\sqrt{${p}}` : `${absA}\\sqrt{${p}}`;
  if (constTerm === 0) return alpha > 0 ? rootPart : `-${rootPart}`;
  const sign = alpha > 0 ? " + " : " - ";
  return `${constTerm}${sign}${rootPart}`;
}

export function generateTask() {
  const p = pick([2, 3, 5]);
  const b = pick([1, 2, 3]);
  const c = pick([2, 3]);
  const binSign = Math.random() < 0.5 ? 1 : -1; // (sqrt(p) +/- b)
  const rootSign = Math.random() < 0.5 ? 1 : -1; // +/- sqrt(c^2*p)

  const baseConst = p + b * b;
  const crossCoeff = 2 * b * binSign;
  const constTerm = baseConst;
  const alpha = crossCoeff + rootSign * c;

  const sqrtP = `\\sqrt{${p}}`;
  const insideOp = binSign === 1 ? "+" : "-";
  const betweenOp = rootSign === 1 ? "+" : "-";
  const rootRad = p * c * c;
  const exprLatex = `(${sqrtP} ${insideOp} ${b})^{2} ${betweenOp} \\sqrt{${rootRad}}`;
  const correctLatex = formatRootExpr(constTerm, alpha, p);

  return {
    type: "RootBinomialOpenTask",
    data: {
      p,
      b,
      c,
      binSign,
      rootSign,
      rootRad,
      exprLatex,
      constTerm,
      alpha,
      correctLatex,
    },
    tags: ["dzialania", "pierwiastki", "wzory-skroconego-mnozenia"],
  };
}

export function renderTask({ data }) {
  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <p>
        Oblicz wartosc wyrazenia: <InlineMath math={data.exprLatex} />.
      </p>
      <p style={{ opacity: 0.85, marginTop: -6 }}>
        Zapisz wynik w postaci <InlineMath math={"a+b\\sqrt{c}"} /> lub{" "}
        <InlineMath math={"a-b\\sqrt{c}"} />.
      </p>
    </div>
  );
}

export function renderInput(_, userAnswer, onChange, disabled) {
  return (
    <SqrtInput
      value={coerceToString(userAnswer)}
      onChange={onChange}
      disabled={disabled}
      wide={true}
      allowCubeRoot={false}
    />
  );
}

export function checkAnswer({ data }, userAnswer) {
  const expected = evaluateExpression(data.correctLatex);
  const given = evaluateExpression(coerceToString(userAnswer));
  if (!Number.isFinite(expected) || !Number.isFinite(given)) return false;
  return Math.abs(expected - given) < 0.01;
}

export function renderAnswer({ data }) {
  return <InlineMath math={data.correctLatex} />;
}

export function renderUserAnswer(userAnswer) {
  const s = coerceToString(userAnswer);
  if (!s || s.trim() === "") return <i>(brak odpowiedzi)</i>;
  return <InlineMath math={normalizeCanonical(s)} />;
}

export function renderExplanation({ data }) {
  const { p, b, c, binSign, rootSign, rootRad, exprLatex, correctLatex } = data;

  const sqrtP = `\\sqrt{${p}}`;
  const formula =
    binSign === 1
      ? "(a+b)^2=a^2+2ab+b^2"
      : "(a-b)^2=a^2-2ab+b^2";
  const crossSign = binSign === 1 ? "+" : "-";
  const expanded1 = `(\\sqrt{${p}} ${binSign === 1 ? "+" : "-"} ${b})^2 = (\\sqrt{${p}})^2 ${crossSign} 2\\cdot ${b}\\sqrt{${p}} + ${b}^2`;
  const expanded2 = `${p} ${crossSign} ${2 * b}\\sqrt{${p}} + ${b * b}`;
  const expanded3 =
    binSign === 1
      ? `${p + b * b} + ${2 * b}\\sqrt{${p}}`
      : `${p + b * b} - ${2 * b}\\sqrt{${p}}`;
  const rootSimpl = `\\sqrt{${rootRad}} = \\sqrt{${c}^2\\cdot ${p}} = ${c}\\sqrt{${p}}`;
  const mergeLine = `${expanded3} ${rootSign === 1 ? "+" : "-"} ${c}\\sqrt{${p}}`;

  return (
    <div className="space-y-2">
      <p>
        <strong>Krok 1.</strong> Rozwijamy kwadrat dwumianu wzorem{" "}
        <InlineMath math={formula} />.
      </p>
      <BlockMath math={expanded1} />
      <BlockMath math={`= ${expanded2}`} />
      <BlockMath math={`= ${expanded3}`} />

      <p>
        <strong>Krok 2.</strong> Upraszczamy pierwiastek.
      </p>
      <BlockMath math={rootSimpl} />

      <p>
        <strong>Krok 3.</strong> Laczymy wyrazenia podobne przy{" "}
        <InlineMath math={sqrtP} />.
      </p>
      <BlockMath math={`${exprLatex} = ${mergeLine}`} />
      <BlockMath math={`= ${correctLatex}`} />
    </div>
  );
}

